<template>
  <div class="container">
    <section class="section">
      <h1 class="title">{{ $t("ContactForm") }}</h1>
      <p class="subtitle">{{ $t("ContactFormSubTitle") }}</p>
      <form class="columns is-justify-content-space-between">
        <div class="column is-half is-6">
          <b-field :label="$t('Subject')">
            <b-select
              :placeholder="$t('SelectSubject')"
              expanded
              v-model="formData.subject"
              data-test-id="subject"
            >
              <option value="-">-</option>
              <option :value="$t('GeneralQuestion')">
                {{ $t("GeneralQuestion") }}
              </option>
              <option :value="$t('Issue')">{{ $t("Issue") }}</option>
              <option :value="$t('Request')">{{ $t("Request") }}</option>
              <option :value="$t('Feedback')">{{ $t("Feedback") }}</option>
            </b-select>
          </b-field>
          <b-field :label="$t('Title')">
            <b-input v-model="formData.title" data-test-id="title" />
          </b-field>
          <b-field :label="$t('Email')">
            <b-input
              type="email"
              v-model="formData.email"
              :validation-message="
                $t('FieldIsInvalid', { fieldName: $t('Email') })
              "
              name="email"
              data-test-id="email"
            />
          </b-field>
          <phoneNumberInput
            v-model="formData.phoneNumber"
            :canEdit="true"
            :label="$t('PhoneNumber')"
            :placeholder="$t('PhoneNumberInput')"
          />
        </div>
        <div class="column is-half is-5">
          <b-field :label="$t('DetailedDescription')">
            <b-input
              type="textarea"
              v-model="formData.description"
              data-test-id="description"
            />
          </b-field>
          <b-field class="is-grouped is-grouped-right">
            <b-button
              class="is-primary"
              @click="submitFormData"
              data-test-id="submit"
              :loading="processing"
              :disabled="processing"
            >
              {{ $t("Submit") }}
            </b-button>
          </b-field>
          <h2 class="title is-size-5 mt-6 mb-3">
            {{ $t("AdditionalContactDetails") }}
          </h2>
          <div class="level is-justify-content-space-between">
            <div class="is-justify-content-start level-item">
              <b-icon pack="fas" icon="phone" size="is-small" />
              <a
                class="has-text-weight-semibold mr-1 ml-1 bidi-plaintext"
                href="tel:+972776001701"
                >+972776001701</a
              >
            </div>
            <div class="is-justify-content-end level-item">
              <b-icon pack="fas" icon="envelope" size="is-small" />
              <a
                class="has-text-weight-semibold mr-1 ml-1"
                href="mailto:msl@mallshoplink.com"
                >msl@mallshoplink.com</a
              >
            </div>
          </div>
        </div>
      </form>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import * as Sentry from '@sentry/browser'
import contactService from '@/services/contactService'
import phoneNumberInput from '@/components/phoneNumberInput.vue'
import { useNotifications } from '@/composables/useNotifications'
import type { ContactFormData } from '@/types/contact'
import { createEmptyContactForm } from '@/types/contact'

const { t: $t } = useI18n()
const { openNotification } = useNotifications()

// Reactive state
const formData = ref<ContactFormData>(createEmptyContactForm())
const processing = ref<boolean>(false)

const submitFormData = async (): Promise<void> => {
  try {
    const validationResults = contactService.validateFormData(
      { ...formData.value }
    )
    
    if (validationResults.length > 0) {
      const translatedErrors = validationResults
        .map((validator) => $t(validator))
        .join(', ')
      
      openNotification({
        message: translatedErrors,
        type: 'is-danger',
        'data-test-id': 'contactUsValidationMessage',
      })
      return
    }

    processing.value = true

    await contactService.postForm({ ...formData.value })
    
    openNotification({
      message: $t('ContactFormsubmitSuccess'),
      type: 'is-success',
    })
    
    // Reset form after successful submission
    formData.value = createEmptyContactForm()
    
  } catch (error: unknown) {
    const err = error as { response?: { status: number; data?: { errors?: Record<string, unknown> } } }
    
    if (err?.response && err.response.status === 409) {
      const errors = err.response.data?.errors
      if (errors) {
        Object.getOwnPropertyNames(errors).forEach((errorKey) => {
          openNotification({
            message: $t(errorKey),
            type: 'is-danger',
          })
        })
      }
    } else {
      openNotification({
        message: $t('ErrorContactUsPage'),
        type: 'is-danger',
      })
      Sentry.captureException(error)
    }
  } finally {
    processing.value = false
  }
}
</script>

